import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, MapPin } from 'lucide-react';
import { useState } from 'react';
import { EmptySlot } from '../components/EmptyState';
import { HelpHint } from '../components/HelpHint';
import { api } from '../lib/api';

/**
 * The admin's side of the field workforce: who turned up, what they spent,
 * who wants time off.
 *
 * Approvals lead, because they are the only part with somebody waiting on the
 * other end. Attendance is underneath because it is read rather than acted on.
 */
export function WorkforcePage() {
  const [tab, setTab] = useState<'approvals' | 'attendance'>('approvals');

  return (
    <div className="space-y-5 pb-10">
      <div>
        <h2 className="font-heading text-2xl">Field team</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Attendance, expense claims and leave.
        </p>
      </div>

      <div className="flex gap-1">
        {(
          [
            ['approvals', 'Waiting for you'],
            ['attendance', 'Attendance'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`rounded-lg border px-3 py-1.5 text-sm ${
              tab === id
                ? 'border-primary-500 bg-primary-50 text-primary-800'
                : 'border-border text-muted-foreground hover:border-muted-foreground'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'approvals' ? (
        <>
          <ExpenseApprovals />
          <LeaveApprovals />
        </>
      ) : (
        <AttendanceRoster />
      )}
    </div>
  );
}

// ─── Expenses ───────────────────────────────────────────────────────────────

function ExpenseApprovals() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const { data } = useQuery({
    queryKey: ['expenses', 'pending'],
    queryFn: () => api.expenses({ status: 'pending' }),
  });

  const decide = useMutation({
    mutationFn: ({ id, decision, note }: { id: string; decision: 'approved' | 'rejected'; note?: string }) =>
      api.decideExpense(id, decision, note),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['expenses'] }),
    onError: (e) => setError((e as Error).message),
  });

  const pending = data?.expenses ?? [];

  return (
    <section className="card">
      <h3 className="mb-1 flex items-center gap-1.5 font-heading text-lg">
        Expense claims ({pending.length})
        <HelpHint>
          Approved claims feed the reports. A refusal needs a reason — that is the part that gets
          argued about later.
        </HelpHint>
      </h3>

      {error && <p className="mb-3 rounded-xl bg-danger-50 p-3 text-sm text-danger-700">{error}</p>}

      {pending.length === 0 ? (
        <EmptySlot headline="Nothing waiting" body="Claims and requests appear here as your team submits them." />
      ) : (
        <ul className="divide-y divide-border">
          {pending.map((e) => (
            <li key={e.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-medium">{e.technician?.name}</span>
                  <span className="tabular-nums font-semibold">KES {e.amountKes}</span>
                  <span className="badge bg-muted text-muted-foreground">{e.category}</span>
                </div>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {new Date(e.spentOn).toLocaleDateString('en-KE')}
                  {e.description && ` · ${e.description}`}
                  {e.job && ` · ${e.job.reference}`}
                </p>
                {!e.attachmentId && (
                  <p className="mt-0.5 text-xs text-accent-700">No receipt attached.</p>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn-primary px-3 py-1.5 text-sm"
                  disabled={decide.isPending}
                  onClick={() => {
                    setError(null);
                    decide.mutate({ id: e.id, decision: 'approved' });
                  }}
                >
                  Approve
                </button>
                <button
                  type="button"
                  className="btn-secondary px-3 py-1.5 text-sm"
                  disabled={decide.isPending}
                  onClick={() => {
                    const note = window.prompt('Why is it refused? They will ask.');
                    if (!note?.trim()) return;
                    setError(null);
                    decide.mutate({ id: e.id, decision: 'rejected', note });
                  }}
                >
                  Refuse
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── Leave ──────────────────────────────────────────────────────────────────

function LeaveApprovals() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['leave', 'pending'],
    queryFn: () => api.leave({ status: 'pending' }),
  });

  // Only fetched when a row is opened: approving blind is the failure mode, but
  // so is loading the impact of twenty requests nobody is looking at.
  const { data: impact } = useQuery({
    queryKey: ['leave-impact', expanded],
    queryFn: () => api.leaveImpact(expanded!),
    enabled: Boolean(expanded),
  });

  const decide = useMutation({
    mutationFn: ({ id, decision, note }: { id: string; decision: 'approved' | 'rejected'; note?: string }) =>
      api.decideLeave(id, decision, note),
    onSuccess: () => {
      setExpanded(null);
      queryClient.invalidateQueries({ queryKey: ['leave'] });
    },
    onError: (e) => setError((e as Error).message),
  });

  const pending = data?.requests ?? [];

  return (
    <section className="card">
      <h3 className="mb-1 flex items-center gap-1.5 font-heading text-lg">
        Leave requests ({pending.length})
        <HelpHint>
          Approving removes them from dispatch suggestions for those days, so nobody has to
          remember on the morning.
        </HelpHint>
      </h3>

      {error && <p className="mb-3 rounded-xl bg-danger-50 p-3 text-sm text-danger-700">{error}</p>}

      {pending.length === 0 ? (
        <EmptySlot headline="Nothing waiting" body="Claims and requests appear here as your team submits them." />
      ) : (
        <ul className="divide-y divide-border">
          {pending.map((r) => (
            <li key={r.id} className="py-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <span className="font-medium">{r.technician?.name}</span>
                  <span className="ml-2 badge bg-muted text-muted-foreground">{r.type}</span>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {new Date(r.startDate).toLocaleDateString('en-KE')} –{' '}
                    {new Date(r.endDate).toLocaleDateString('en-KE')} · {r.days} day{r.days === 1 ? '' : 's'} away
                    {/* And what it costs, which is the number that comes off a balance.
                        Friday to Monday is four days away and two days of leave. */}
                    {r.workingDays !== undefined && r.workingDays !== r.days && (
                      <> · {r.workingDays} working day{r.workingDays === 1 ? '' : 's'}</>
                    )}
                  </p>
                  {r.reason && <p className="mt-0.5 text-sm">{r.reason}</p>}
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-secondary px-3 py-1.5 text-sm"
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                  >
                    {expanded === r.id ? 'Hide' : 'Check cover'}
                  </button>
                  <button
                    type="button"
                    className="btn-primary px-3 py-1.5 text-sm"
                    disabled={decide.isPending}
                    onClick={() => {
                      setError(null);
                      decide.mutate({ id: r.id, decision: 'approved' });
                    }}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="btn-secondary px-3 py-1.5 text-sm"
                    disabled={decide.isPending}
                    onClick={() => {
                      const note = window.prompt('Why? They have made plans.');
                      if (!note?.trim()) return;
                      setError(null);
                      decide.mutate({ id: r.id, decision: 'rejected', note });
                    }}
                  >
                    Refuse
                  </button>
                </div>
              </div>

              {expanded === r.id && impact && (
                <div className="mt-3 rounded-xl bg-muted/40 p-3 text-sm">
                  <p>
                    <span className="font-medium">{impact.openJobs}</span> open job
                    {impact.openJobs === 1 ? '' : 's'} currently on their name.
                  </p>
                  {impact.alsoOff.length > 0 ? (
                    <p className="mt-1">
                      Also off then: {impact.alsoOff.map((a) => a.name).join(', ')}
                    </p>
                  ) : (
                    <p className="mt-1 text-muted-foreground">Nobody else is off those days.</p>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── Attendance ─────────────────────────────────────────────────────────────

function AttendanceRoster() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const { data, isPending } = useQuery({
    queryKey: ['attendance-roster', date],
    queryFn: () => api.attendanceRoster(date),
  });

  if (isPending) return <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-primary-500" />;

  return (
    <section className="card">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-1.5 font-heading text-lg">
          {data?.present} of {data?.total} on shift
          <HelpHint>
            The whole roster, including who has not checked in — the useful question is who is
            missing, and a list of people who turned up cannot answer it.
          </HelpHint>
        </h3>
        <input
          type="date"
          className="input"
          value={date}
          max={new Date().toISOString().slice(0, 10)}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>

      <ul className="divide-y divide-border text-sm">
        {(data?.roster ?? []).map((row) => (
          <li key={row.technicianId} className="flex flex-wrap items-center justify-between gap-3 py-2.5">
            <div>
              <span className="font-medium">{row.name}</span>
              <span className="ml-2 text-muted-foreground">{row.phone}</span>
              {row.note && <p className="text-xs text-muted-foreground">{row.note}</p>}
            </div>

            <div className="flex items-center gap-3">
              {row.checkInAt && (
                <span className="tabular-nums text-muted-foreground">
                  {new Date(row.checkInAt).toLocaleTimeString('en-KE', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  {row.checkOutAt &&
                    ` – ${new Date(row.checkOutAt).toLocaleTimeString('en-KE', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}`}
                </span>
              )}
              {row.checkInLat && row.checkInLng && (
                <a
                  className="text-primary-600 hover:underline"
                  href={`https://www.google.com/maps?q=${row.checkInLat},${row.checkInLng}`}
                  target="_blank"
                  rel="noreferrer"
                  title="Where they checked in"
                >
                  <MapPin className="h-4 w-4" aria-hidden="true" />
                </a>
              )}
              <span
                className={`badge shrink-0 ${
                  row.status === 'working'
                    ? 'bg-secondary-50 text-secondary-700'
                    : row.status === 'finished'
                      ? 'bg-muted text-muted-foreground'
                      : 'bg-accent-50 text-accent-700'
                }`}
              >
                {row.status === 'absent' ? 'not in' : row.status}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
